/**
 * Construction-themed SVG background patterns for sections.
 */

type Variant = "blueprint" | "roofline" | "grid" | "tealprint" | "darkgrid";

const BlueprintPattern = () => (
  <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="blueprint-grid" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
        <line x1="0" y1="0" x2="60" y2="0" stroke="currentColor" strokeWidth="0.5" opacity="0.15" />
        <line x1="0" y1="0" x2="0" y2="60" stroke="currentColor" strokeWidth="0.5" opacity="0.15" />
        <line x1="30" y1="0" x2="30" y2="60" stroke="currentColor" strokeWidth="0.3" opacity="0.08" />
        <line x1="0" y1="30" x2="60" y2="30" stroke="currentColor" strokeWidth="0.3" opacity="0.08" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#blueprint-grid)" />
    <g opacity="0.1" stroke="currentColor" strokeWidth="1.5" fill="none">
      <polyline points="80,200 130,160 180,200" />
      <polyline points="300,120 360,75 420,120" />
      <polyline points="600,280 660,235 720,280" />
      <polyline points="900,150 960,105 1020,150" />
      <polyline points="150,400 210,355 270,400" />
      <polyline points="500,80 560,40 620,80" />
      <polyline points="800,350 860,305 920,350" />
      <polyline points="1100,220 1160,175 1220,220" />
    </g>
  </svg>
);

const RooflinePattern = () => (
  <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="roofline-pattern" x="0" y="0" width="180" height="90" patternUnits="userSpaceOnUse">
        <polyline points="0,70 45,40 90,70" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.08" />
        <polyline points="90,70 135,40 180,70" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.08" />
        <line x1="45" y1="40" x2="45" y2="70" stroke="currentColor" strokeWidth="0.5" opacity="0.06" />
        <line x1="135" y1="40" x2="135" y2="70" stroke="currentColor" strokeWidth="0.5" opacity="0.06" />
        <rect x="30" y="55" width="10" height="15" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.05" />
        <rect x="120" y="55" width="10" height="15" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.05" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#roofline-pattern)" />
  </svg>
);

const GridPattern = () => (
  <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="construction-dots" x="0" y="0" width="30" height="30" patternUnits="userSpaceOnUse">
        <circle cx="15" cy="15" r="1.2" fill="currentColor" opacity="0.12" />
      </pattern>
      <pattern id="diagonal-lines" x="0" y="0" width="20" height="20" patternUnits="userSpaceOnUse">
        <line x1="0" y1="20" x2="20" y2="0" stroke="currentColor" strokeWidth="0.4" opacity="0.08" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#construction-dots)" />
    <rect width="100%" height="100%" fill="url(#diagonal-lines)" />
  </svg>
);

// For teal (primary) sections — light colored pattern
const TealPrintPattern = () => (
  <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="teal-grid" x="0" y="0" width="50" height="50" patternUnits="userSpaceOnUse">
        <line x1="0" y1="0" x2="50" y2="0" stroke="currentColor" strokeWidth="0.5" opacity="0.12" />
        <line x1="0" y1="0" x2="0" y2="50" stroke="currentColor" strokeWidth="0.5" opacity="0.12" />
      </pattern>
      <pattern id="teal-diamonds" x="0" y="0" width="40" height="40" patternUnits="userSpaceOnUse">
        <polygon points="20,5 35,20 20,35 5,20" stroke="currentColor" strokeWidth="0.5" fill="none" opacity="0.08" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#teal-grid)" />
    <rect width="100%" height="100%" fill="url(#teal-diamonds)" />
  </svg>
);

// For dark (foreground) sections — light colored pattern
const DarkGridPattern = () => (
  <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
    <defs>
      <pattern id="dark-dots" x="0" y="0" width="24" height="24" patternUnits="userSpaceOnUse">
        <circle cx="12" cy="12" r="0.8" fill="currentColor" opacity="0.1" />
      </pattern>
      <pattern id="dark-cross" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
        <line x1="28" y1="25" x2="32" y2="25" stroke="currentColor" strokeWidth="0.5" opacity="0.08" />
        <line x1="30" y1="23" x2="30" y2="27" stroke="currentColor" strokeWidth="0.5" opacity="0.08" />
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#dark-dots)" />
    <rect width="100%" height="100%" fill="url(#dark-cross)" />
  </svg>
);

const patterns: Record<Variant, () => JSX.Element> = {
  blueprint: BlueprintPattern,
  roofline: RooflinePattern,
  grid: GridPattern,
  tealprint: TealPrintPattern,
  darkgrid: DarkGridPattern,
};

interface Props {
  variant: Variant;
  className?: string;
}

const BackgroundPattern = ({ variant, className = "" }: Props) => {
  const Pattern = patterns[variant];
  return (
    <div className={`absolute inset-0 pointer-events-none overflow-hidden ${className}`} aria-hidden="true">
      <Pattern />
    </div>
  );
};

export default BackgroundPattern;
